/**
 * useSignalR Custom Hook
 * Provides SignalR connection management and event subscription for React components
 *
 * Usage:
 * const { isConnected, connectionState, subscribe, unsubscribe } = useSignalR();
 *
 * useEffect(() => {
 *   const handleNewIssue = (notification) => {
 *     console.log('New issue:', notification);
 *   };
 *
 *   subscribe('newIssue', handleNewIssue);
 *   return () => unsubscribe('newIssue', handleNewIssue);
 * }, [subscribe, unsubscribe]);
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { signalRService } from '../services/signalr';

export const useSignalR = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('Disconnected');
  const [isReconnecting, setIsReconnecting] = useState(false);
  const connectionCheckInterval = useRef(null);
  const hasAttemptedConnection = useRef(false);

  /**
   * Initialize SignalR connection when token becomes available
   */
  useEffect(() => {
    const initializeConnection = async () => {
      try {
        const token = localStorage.getItem('accessToken');

        if (!token) {
          // No token available - user not authenticated yet
          // Reset the connection attempt flag
          hasAttemptedConnection.current = false;
          return;
        }

        // Only attempt connection once per authentication session
        if (hasAttemptedConnection.current) {
          return;
        }

        hasAttemptedConnection.current = true;

        // Start connection if not already connected
        if (!signalRService.isConnected()) {
          await signalRService.start(token);
          setIsConnected(true);
          setConnectionState('Connected');
          console.log('[useSignalR] Successfully connected to SignalR');
        }
      } catch (error) {
        // Silently handle connection failures - they're expected when:
        // - Server is not running
        // - Token is invalid/expired
        // - Network issues
        setIsConnected(false);
        setConnectionState('Disconnected');
      }
    };

    initializeConnection();

    // Setup connection state monitoring
    const monitorConnection = () => {
      const state = signalRService.getConnectionState();
      const connected = signalRService.isConnected();

      setConnectionState(state || 'Disconnected');
      setIsConnected(connected);
      setIsReconnecting(state === 'Reconnecting');
    };

    // Check connection state every 5 seconds
    connectionCheckInterval.current = setInterval(monitorConnection, 5000);

    // Cleanup on unmount
    return () => {
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }
      // Note: Don't stop the connection on unmount since it's a singleton
      // It should persist across component lifecycles
    };
  }, []);

  /**
   * Subscribe to SignalR events
   * @param {string} eventType - Event type (newIssue, newMessage, statusChange, etc.)
   * @param {Function} callback - Callback function to execute when event occurs
   */
  const subscribe = useCallback((eventType, callback) => {
    if (!eventType || typeof callback !== 'function') {
      console.error('[useSignalR] Invalid subscription parameters');
      return;
    }

    signalRService.on(eventType, callback);
    console.log(`[useSignalR] Subscribed to ${eventType}`);
  }, []);

  /**
   * Unsubscribe from SignalR events
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function to remove
   */
  const unsubscribe = useCallback((eventType, callback) => {
    if (!eventType || typeof callback !== 'function') {
      console.error('[useSignalR] Invalid unsubscription parameters');
      return;
    }

    signalRService.off(eventType, callback);
    console.log(`[useSignalR] Unsubscribed from ${eventType}`);
  }, []);

  /**
   * Join a specific issue room for real-time updates
   * @param {number} reportId - Issue report ID
   */
  const joinIssueRoom = useCallback(async (reportId) => {
    if (!reportId) {
      console.error('[useSignalR] Invalid reportId for joinIssueRoom');
      return;
    }

    try {
      await signalRService.joinIssueRoom(reportId);
      console.log(`[useSignalR] Joined issue room ${reportId}`);
    } catch (error) {
      console.error(`[useSignalR] Failed to join issue room ${reportId}:`, error);
    }
  }, []);

  /**
   * Leave a specific issue room
   * @param {number} reportId - Issue report ID
   */
  const leaveIssueRoom = useCallback(async (reportId) => {
    if (!reportId) {
      console.error('[useSignalR] Invalid reportId for leaveIssueRoom');
      return;
    }

    try {
      await signalRService.leaveIssueRoom(reportId);
      console.log(`[useSignalR] Left issue room ${reportId}`);
    } catch (error) {
      console.error(`[useSignalR] Failed to leave issue room ${reportId}:`, error);
    }
  }, []);

  /**
   * Manually reconnect to SignalR
   */
  const reconnect = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        // No token available - cannot reconnect
        return;
      }

      await signalRService.stop();
      await signalRService.start(token);

      setIsConnected(true);
      setConnectionState('Connected');
      console.log('[useSignalR] Manually reconnected');
    } catch (error) {
      // Silently handle reconnection failures
      setIsConnected(false);
      setConnectionState('Disconnected');
    }
  }, []);

  return {
    isConnected,
    connectionState,
    isReconnecting,
    subscribe,
    unsubscribe,
    joinIssueRoom,
    leaveIssueRoom,
    reconnect
  };
};

export default useSignalR;
